import React, { Component, ReactElement, ReactNode } from 'react';

import moment from 'moment';

import { DateFieldAPI, RangeState } from '@philips-emr/tws-components-api';
import { Range } from '@philips-emr/tws-components-api/lib/api/calendar/calendar-api';
import { ButtonTheme, Expression } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { array, arrayOf, bool, eventListener, expression, oneOf, oneOfType, shape, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { ChangeEvent } from '@philips-emr/tws-core/lib/component/types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { format } from '@philips-emr/tws-core/lib/services/model-service';
import { enumValues, isFunction, isMobile } from '@philips-emr/tws-utils';

import { Button, DatePicker, Popover, SlideUpPanel } from '..';
import { ReactComponent as CalendarIcon } from '../../../resources/assets/components/calendar.svg';

import { DateFieldBasic, DateFieldInterval } from './components';

import './date-field.scss';

function parseDate(value: string): string {
  return moment.utc(value).format('YYYY-MM-DD');
}

function parseDates(values: string[]): string[] {
  return values.map(parseDate);
}

function parseValue(value: string | string[]): string | string[] {
  if (Array.isArray(value)) {
    return parseDates(value);
  }

  return parseDate(value);
}

interface DateFieldProps {
  applyExpression?: string;
  clearExpression?: string;
  data: object[];
  enabled: boolean;
  initialDate: string;
  interval: boolean;
  noAppointmentsExpression?: string;
  onChange: (event: ChangeEvent<string | string[] | undefined>) => void;
  placeholder?: string;
  ranges: Range[];
  rangeState: RangeState;
  readOnly: boolean;
  todayExpression?: string;
  valid: boolean;
  value: string | string[];
}

interface DateFieldState {
  showModal: boolean;
  tempValue?: string[];
}

@AwesomeComponent({ type: ComponentType.DATA_ENTRY, api: DateFieldAPI })
class DateField extends Component<DateFieldProps, DateFieldState> {

  static propTypes = {
    applyExpression: expression,
    clearExpression: expression,
    data: array,
    initialDate: string,
    interval: bool,
    noAppointmentsExpression: expression,
    onChange: eventListener,
    placeholder: expression,
    ranges: arrayOf(shape({ startDate: string, endDate: string })).notEnumerable(),
    rangeState: oneOf(enumValues(RangeState)),
    readOnly: bool,
    todayExpression: expression,
    valid: bool,
    value: oneOfType([string, arrayOf(string)]),
  };

  static defaultProps = {
    applyExpression: Expression.APPLY,
    clearExpression: Expression.CLEAR,
    interval: false,
    rangeState: RangeState.DISABLED,
    ranges: [],
    todayExpression: Expression.TODAY,
    valid: true,
  };

  state: DateFieldState = {
    showModal: false,
  };

  fireChange(value: string | string[]): void {
    const { onChange, value: oldValue } = this.props;

    if (isFunction(onChange)) {
      onChange(new ChangeEvent({
        value: value,
        oldValue: oldValue,
      }));
    }
  }

  onChange = (event: ChangeEvent<string | string[]>): void => {
    this.fireChange(event.value);
  };

  onDatePickerChange = (event: ChangeEvent<string | string[]>): void => {
    const { value } = event;

    if (isMobile()) {
      this.setState({
        tempValue: [value].flat(),
      });
    } else {
      this.closeModal();
      this.fireChange(value ? parseValue(value) : value);
    }
  };

  applyValue = (): void => {
    this.closeModal();
    this.fireChange(this.getModalValue());
  };

  clearValue = (): void => {
    this.setState({ tempValue: [] });
  };

  selectToday = (): void => {
    this.setState({ tempValue: [moment().utc().toISOString()] });
  };

  closeModal = (): void => {
    this.setState({
      showModal: false,
      tempValue: undefined,
    });
  };

  openModal = (): void => {
    this.setState({ showModal: true });
  };

  getIntervalValue = (): string[] => {
    const { value } = this.props;

    return [value].flat();
  };

  getValue = (): string => {
    const { value } = this.props;

    return Array.isArray(value) ? value[0] : value;
  };

  getModalValue(): string | string[] {
    const { interval, value } = this.props;
    const { tempValue } = this.state;

    const modalValue = tempValue ? parseValue(tempValue) : value;

    if (interval) {
      return modalValue ? [modalValue].flat() : [];
    }

    return Array.isArray(modalValue) ? modalValue[0] : modalValue;
  }

  renderDatePicker = (interval: boolean, externalControls: boolean = false): ReactElement => {
    const { applyExpression, clearExpression, data, initialDate, rangeState, noAppointmentsExpression, ranges, todayExpression } = this.props;

    const expressions = {
      applyExpression,
      clearExpression,
      noAppointmentsExpression,
      todayExpression,
    };

    return (
      <DatePicker
        {...expressions}
        data={data}
        externalControls={externalControls}
        initialDate={initialDate}
        interval={interval}
        rangeState={rangeState}
        onChange={this.onDatePickerChange}
        ranges={ranges}
        value={this.getModalValue()}
      />
    );
  };

  getCalendarIcon(): ReactElement | null {
    const { readOnly } = this.props;

    if (readOnly) {
      return null;
    }

    if (isMobile()) {
      return (
        <div className="c-date-field__calendar" onClick={this.openModal} >
          <CalendarIcon />
        </div>
      );
    }

    return this.renderDesktopCalendar();
  }

  renderDesktopCalendar(): ReactElement {
    const { interval } = this.props;
    const { showModal } = this.state;

    return (
      <Popover open={showModal} onClose={this.closeModal} >
        <div className="c-date-field__calendar" x-contrast="bg" onClick={this.openModal} >
          <CalendarIcon />
        </div>

        <Popover.Content>
          {this.renderDatePicker(interval)}
        </Popover.Content>
      </Popover>
    );
  }

  renderPanelHeaderContent(): ReactNode {
    const modalValue = this.getModalValue();

    if (!modalValue || modalValue.length === 0) {
      return <div className="c-date-field__modal__header__message">{msg('tws.date-field.select-date')}</div>;
    }

    const selectedDate = Array.isArray(modalValue) ? modalValue[0] : modalValue;

    const year = format<string>('date', selectedDate, { type: 'shortYear' });
    const date = format<string>('date', selectedDate, { type: 'datepicker' });

    return (
      <>
        <div className="c-date-field__modal__header__year">{year}</div>
        <div className="c-date-field__modal__header__date">{date}</div>
      </>
    );
  }

  renderSecondaryButtons(): ReactElement {
    const { clearExpression, todayExpression } = this.props;
    const modalValue = this.getModalValue();

    if (modalValue?.length > 0) {
      return <Button onClick={this.clearValue} theme={ButtonTheme.SECONDARY} value={clearExpression} />;
    }

    return <Button onClick={this.selectToday} theme={ButtonTheme.SECONDARY} value={todayExpression} />;
  }

  renderMobileCalendar(): ReactElement {
    const { applyExpression, interval } = this.props;
    const { showModal } = this.state;

    return (
      <SlideUpPanel closeable={true} open={showModal} onClose={this.closeModal}>
        <SlideUpPanel.Header>
          {this.renderPanelHeaderContent()}
        </SlideUpPanel.Header>
        <SlideUpPanel.Content>
          <div className="c-date-field__modal__content">
            {this.renderDatePicker(interval, true)}
          </div>
        </SlideUpPanel.Content>
        <SlideUpPanel.Footer>
          <div className="c-date-field__modal__footer">
            {this.renderSecondaryButtons()}
            <Button onClick={this.applyValue} value={applyExpression}></Button>
          </div>
        </SlideUpPanel.Footer>
      </SlideUpPanel>
    );
  }

  renderCalendar(): ReactElement | null {
    const { readOnly } = this.props;

    if (readOnly || !isMobile()) {
      return null;
    }

    return this.renderMobileCalendar();
  }

  renderDateField(): ReactElement {
    const { enabled, interval, placeholder, readOnly, valid } = this.props;

    if (interval) {
      const [startDate, endDate] = this.getIntervalValue();

      return (
        <DateFieldInterval
          calendar={this.getCalendarIcon()}
          enabled={enabled}
          endDate={endDate}
          onClick={this.openModal}
          placeholder={placeholder}
          readOnly={readOnly}
          startDate={startDate}
          valid={valid}
        />
      );
    }

    return (
      <DateFieldBasic
        calendar={this.getCalendarIcon()}
        date={this.getValue()}
        enabled={enabled}
        onChange={this.onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        valid={valid}
      />
    );
  }

  render(): ReactElement {
    return (
      <div className="c-date-field">
        <div className="c-date-field__container">
          {this.renderDateField()}
        </div>

        {this.renderCalendar()}
      </div>
    );
  }
}

export default DateField;
